import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation, UNSAFE_NavigationContext } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import InterviewVideoCall from './components/InterviewVideoCall';
import UnifiedWorkspace from './components/UnifiedWorkspace';
import InterviewTimer from './components/InterviewTimer';
import SpeakingAvatar from './components/SpeakingAvatar';
import AudioVisualizer from './components/AudioVisualizer';
import { getApiBaseUrl, endInterviewSession, getSession, submitInterviewFeedback, executeInterviewCode } from '../../utils/api';
import FeedbackModal from './components/FeedbackModal';
import IssueReportModal from './components/IssueReportModal';
import sttService from '../../utils/stt';

const DynamicCaption = ({ text, isVisible }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setDisplayedText("");
    setIndex(0);
  }, [text]);

  useEffect(() => {
    if (!isVisible || !text) return;
    if (index < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + text[index]);
        setIndex((prev) => prev + 1);
      }, 45); // Adjusted speed (35ms) to better match speech
      return () => clearTimeout(timeout);
    }
  }, [index, text, isVisible]);

  if (!text || !isVisible) return null;

  return (
    <div className="absolute bottom-28 left-0 right-0 mx-auto w-full max-w-2xl px-6 flex justify-center z-[100] pointer-events-none transition-all duration-300 animate-fade-in-up">
      <div className="bg-white/90 dark:bg-[#1A1A1A]/95 backdrop-blur-md rounded-2xl p-4 shadow-2xl border border-white/10 dark:border-white/10 text-center flex items-center justify-center max-h-[140px] overflow-hidden group">
        <p className="text-[15px] text-slate-900 dark:text-gray-100 font-medium leading-relaxed tracking-wide inline-flex items-center">
          {displayedText}
          {index < text.length && <span className="inline-block w-1.5 h-4 ml-1 bg-emerald-400 animate-pulse" />}
        </p>
      </div>
    </div>
  );
};
// Using backend STT/TTS. Frontend will capture/send raw audio blobs and play server audio.

// Custom hook to block navigation - React Router v6.0.2
function useBlocker(blocker, when = true) {
  const { navigator } = React.useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    if (!when) return;

    const unblock = navigator.block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        },
      };
      blocker(autoUnblockingTx);
    });

    return unblock;
  }, [navigator, blocker, when]);
}

// ── Random Interviewer Assignment ──
const INTERVIEWER_NAMES = [
  'Ravi', 'Hritik', 'Aman', 'Shashwat'
];

const ROLE_BY_TYPE = {
  coding: ['Senior Software Engineer'],
  system_design: ['Staff Engineer', 'Senior Software Engineer'],
  behavioral: ['Tech Lead'],
  mixed: ['Senior Software Engineer'],
};

const INTEGRITY_RULES = {
  tab_switch: {
    severity: 'high',
    message: 'Tab or window switch detected',
  },
  copy_attempt: {
    severity: 'high',
    message: 'Copy or cut attempt blocked',
  },
  paste: {
    severity: 'high',
    message: 'Paste attempt blocked',
  },
  selection_attempt: {
    severity: 'medium',
    message: 'Question text selection blocked',
  },
};

const getIntegrityStorageKey = (sessionId) => `prepxai_integrity_events_${sessionId}`;
const getWorkspaceStorageKey = (sessionId) => `prepxai_interview_workspace_${sessionId}`;

const didPageReload = () => {
  if (typeof window === 'undefined' || typeof performance === 'undefined') return false;
  const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
  return navigationEntry
    ? navigationEntry.type === 'reload'
    : performance.navigation?.type === performance.navigation?.TYPE_RELOAD;
};

const readStoredCodeSnapshot = (sessionId) => {
  if (!sessionId || typeof localStorage === 'undefined') return null;
  try {
    const stored = JSON.parse(localStorage.getItem(getWorkspaceStorageKey(sessionId)) || 'null');
    return stored?.latest && typeof stored.latest.code === 'string'
      ? stored.latest
      : null;
  } catch (error) {
    return null;
  }
};

const readStoredIntegrityEvents = (sessionId) => {
  if (!sessionId || typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(getIntegrityStorageKey(sessionId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeStoredIntegrityEvents = (sessionId, events) => {
  if (!sessionId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(getIntegrityStorageKey(sessionId), JSON.stringify(events.slice(0, 100)));
  } catch (error) {
    // Ignore storage quota/private mode issues; WebSocket submission still runs.
  }
};

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const AnimatedTranscriptEntry = ({ text, speaker, timestamp, confidence, interviewerName }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (speaker !== 'interviewer') {
      setDisplayedText(text);
      return;
    }
    if (index < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + text[index]);
        setIndex((prev) => prev + 1);
      }, 40); // Snappier typing speed (~250wpm)
      return () => clearTimeout(timeout);
    }
  }, [index, text, speaker]);

  return (
    <div className={`inline-block max-w-[85%] rounded-lg px-3 py-2 shadow-sm ${speaker === 'interviewer'
      ? 'bg-blue-50 border border-blue-200 text-blue-900'
      : speaker === 'you'
        ? 'bg-white border border-gray-200 text-gray-900'
        : 'bg-gray-100 border border-gray-200 text-gray-700'
      }`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-semibold text-xs">
          {speaker === 'interviewer' ? (interviewerName || 'AI') : speaker === 'you' ? 'You' : 'System'}
        </span>
        {confidence && speaker === 'you' && (
          <span className="text-xs text-gray-400">
            ({Math.round(confidence * 100)}%)
          </span>
        )}
        <span className="text-xs text-gray-400">
          {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <p className="break-words whitespace-pre-wrap">
        {displayedText}
        {speaker === 'interviewer' && index < text.length && (
          <span className="inline-block w-1.5 h-4 ml-0.5 bg-blue-400 animate-pulse align-middle" />
        )}
      </p>
    </div>
  );
};

const LiveInterviewPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const sessionId = useMemo(() => {
    return location?.state?.sessionId ||
      JSON.parse(localStorage.getItem('currentInterviewSession') || '{}')?.sessionId ||
      null;
  }, [location?.state]);

  // True when user is returning to a zombie session (tab was closed without ending)
  // A reload opens a new WebSocket connection just like an intentional resume.
  // Tell the server to preserve its current state instead of sending a new intro.
  const isResume = useMemo(() => !!location?.state?.isResume || didPageReload(), [location?.state]);

  const sessionConfig = useMemo(() => {
    const storedConfig = JSON.parse(localStorage.getItem('currentInterviewSession') || '{}');
    // A direct/resumed navigation can carry the config in route state before
    // localStorage is available. Prefer persisted values, while retaining any
    // missing fields from navigation state.
    return { ...(location?.state?.config || {}), ...storedConfig };
  }, [location?.state]);

  const interviewType = useMemo(() => {
    return sessionConfig?.interviewType || 'mixed';
  }, [sessionConfig]);

  const company = useMemo(() => {
    return sessionConfig?.company || '';
  }, [sessionConfig]);

  const interviewer = useMemo(() => {
    const name = pickRandom(INTERVIEWER_NAMES);
    const roles = ROLE_BY_TYPE[interviewType] || ROLE_BY_TYPE.mixed;
    const role = pickRandom(roles);
    const companyDisplay = capitalize(company);
    const tagline = companyDisplay ? `${role} || @ ${companyDisplay}` : role;
    return { name, role, tagline };
  }, [interviewType, company]);

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user_profile')) || { full_name: 'You' };
    } catch (e) {
      return { full_name: 'You' };
    }
  }, []);

  const [isPaused, setIsPaused] = useState(false);
  const [isRecording, setIsRecording] = useState(true);
  const [showIDE, setShowIDE] = useState(false);
  const [showTranscript, setShowTranscript] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoHidden, setIsVideoHidden] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  // Incrementing this opens a fresh socket after an unexpected disconnect.
  // The server restores the existing session when `is_resume` is sent below.
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [interviewStage, setInterviewStage] = useState('intro'); // intro, waiting_intro, intro_processed, question
  const [showEndInterviewModal, setShowEndInterviewModal] = useState(false);
  const [showIssueReportModal, setShowIssueReportModal] = useState(false);
  const [showReportHint, setShowReportHint] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [currentCaption, setCurrentCaption] = useState("");
  const [isEnding, setIsEnding] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState('whiteboard');
  const [isConnecting, setIsConnecting] = useState(true); // Show loading on interviewer avatar
  const [interviewerRevealed, setInterviewerRevealed] = useState(false); // 2s reveal delay
  const [integrityEvents, setIntegrityEvents] = useState(() => readStoredIntegrityEvents(sessionId));
  const [visibleIntegrityEvent, setVisibleIntegrityEvent] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  const mutedRef = useRef(false);
  const isEndingRef = useRef(false);
  const currentCaptionRef = useRef("");
  const sttSource = import.meta.env.VITE_STT_SOURCE || 'third-party';

  useEffect(() => {
    currentCaptionRef.current = currentCaption;
  }, [currentCaption]);

  useEffect(() => {
    isEndingRef.current = isEnding;
  }, [isEnding]);

  useEffect(() => {
    const updateThemeMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    updateThemeMode();
    const observer = new MutationObserver(updateThemeMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const setColorMode = (mode) => {
    const shouldUseDark = mode === 'dark';
    document.documentElement.classList.toggle('dark', shouldUseDark);
    localStorage.setItem('theme', mode);
    setIsDarkMode(shouldUseDark);
  };

  const normalizeSpeechText = (text) => {
    return text?.toLowerCase().replace(/[.,!]/g, '').trim() || '';
  };

  const VAD_SILENCE_MS = 2000;

  const fillerWords = new Set(['ok', 'okay', 'oh', 'ohh', 'got it', 'i see', 'um', 'uh', 'ah', 'right', 'hmm']);
  const isFillerSpeech = (text) => fillerWords.has(normalizeSpeechText(text));

  const ensureGainNode = () => {
    if (!audioContextRef.current) return;
    if (!gainNodeRef.current || gainNodeRef.current.context !== audioContextRef.current) {
      if (gainNodeRef.current) {
        try {
          gainNodeRef.current.disconnect();
        } catch (e) {
          // ignore disconnect errors
        }
      }
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.value = 1;
      gainNodeRef.current.connect(audioContextRef.current.destination);
    }
  };

  const setAIGain = (value) => {
    if (!gainNodeRef.current || !audioContextRef.current) return;
    try {
      gainNodeRef.current.gain.setValueAtTime(value, audioContextRef.current.currentTime);
    } catch (error) {
      gainNodeRef.current.gain.value = value;
    }
  };

  const cancelPendingAIHold = () => {
    if (aiPauseTimerRef.current) {
      clearTimeout(aiPauseTimerRef.current);
      aiPauseTimerRef.current = null;
    }
    if (aiHoldRef.current) {
      aiHoldRef.current = false;
      setAIGain(1);
    }
  };

  const pauseAIPlaybackMomentarily = () => {
    if (!isPlayingRef.current || aiHoldRef.current) return;
    aiHoldRef.current = true;
    ensureGainNode();
    setAIGain(0);
    setIsAISpeaking(false); // Stop mouth animation during pause
    if (aiPauseTimerRef.current) {
      clearTimeout(aiPauseTimerRef.current);
    }
    aiPauseTimerRef.current = window.setTimeout(() => {
      aiHoldRef.current = false;
      setAIGain(1);
      if (isPlayingRef.current) setIsAISpeaking(true); // Resume mouth if still playing
      aiPauseTimerRef.current = null;
      console.log('[BargeIn] No follow-up speech detected during pause; resuming AI playback.');
    }, 1000);
  };

  const confirmLocalInterruption = () => {
    cancelPendingAIHold();
    handleLocalInterruption();
  };

  // Ensure "Assigning you an Interviewer" shows for at least 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => setInterviewerRevealed(true), 2000);

    // Show report hint popover 15 seconds into the interview
    const hintShowTimer = setTimeout(() => setShowReportHint(true), 15000);
    // Auto-dismiss the hint after 5s of display (at 20s total)
    const hintHideTimer = setTimeout(() => setShowReportHint(false), 20000);

    return () => {
      clearTimeout(timer);
      clearTimeout(hintShowTimer);
      clearTimeout(hintHideTimer);
    };
  }, []);

  // Navigation blocking state
  const [pendingTx, setPendingTx] = useState(null);

  const [sessionStartUnix] = useState(() => {
    // Setup and device checks happen after a session is created. Start the
    // timeout clock only once the live interview panel is entered.
    const storedStart = new Date(sessionConfig?.liveStartedAt || '').getTime();
    if (isResume && Number.isFinite(storedStart) && storedStart > 0) {
      return Math.floor(storedStart / 1000);
    }
    return Math.floor(Date.now() / 1000);
  });
  const [timeElapsed, setTimeElapsed] = useState(() => Math.max(0, Math.floor(Date.now() / 1000) - sessionStartUnix));
  const configuredDurationMinutes = Number(sessionConfig?.duration);
  const hasConfiguredDuration = Number.isFinite(configuredDurationMinutes) && configuredDurationMinutes > 0;
  const interviewDurationSeconds = hasConfiguredDuration ? configuredDurationMinutes * 60 : 0;
  const autoEndDeadlineSeconds = hasConfiguredDuration ? interviewDurationSeconds + (3 * 60) : null;
  const autoEndRemainingSeconds = autoEndDeadlineSeconds === null ? 0 : Math.max(0, autoEndDeadlineSeconds - timeElapsed);
  const showAutoEndCountdown = hasConfiguredDuration && timeElapsed >= interviewDurationSeconds && autoEndRemainingSeconds > 0;

  useEffect(() => {
    if (!sessionId) return;

    const storedSession = JSON.parse(localStorage.getItem('currentInterviewSession') || '{}');
    const needsHydration =
      !storedSession.sessionId ||
      !Number.isFinite(Number(storedSession.duration)) ||
      Number(storedSession.duration) <= 0;

    if (needsHydration) {
      localStorage.setItem('currentInterviewSession', JSON.stringify({
        ...storedSession,
        ...sessionConfig,
        sessionId,
      }));
    }
  }, [sessionId, sessionConfig]);

  useEffect(() => {
    const storedSession = JSON.parse(localStorage.getItem('currentInterviewSession') || '{}');
    if (!storedSession.liveStartedAt) {
      localStorage.setItem('currentInterviewSession', JSON.stringify({
        ...storedSession,
        liveStartedAt: new Date(sessionStartUnix * 1000).toISOString(),
      }));
    }
  }, [sessionStartUnix]);

  useEffect(() => {
    if (isPaused) return undefined;
    const updateElapsed = () => setTimeElapsed(Math.max(0, Math.floor(Date.now() / 1000) - sessionStartUnix));
    updateElapsed();
    const interval = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(interval);
  }, [isPaused, sessionStartUnix]);

  useEffect(() => {
    if (autoEndDeadlineSeconds !== null && timeElapsed >= autoEndDeadlineSeconds && !isEnding && sessionId) {
      console.warn('[SessionTimeout] Interview duration exceeded including grace period. Auto-ending.');
      isEndingRef.current = true;
      setIsEnding(true);
      try { stopRecording(); } catch (e) { }

      // Call EndSession to generate the report
      endInterviewSession(sessionId, getIntegrityEventsForSession(), readStoredCodeSnapshot(sessionId)).catch(e => {
        console.error("Failed to end session on auto-timeout:", e);
      });

      localStorage.removeItem('currentInterviewSession');

      // Navigate to report generation page
      navigate('/generating-result', {
        state: { sessionId, autoEnded: true }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeElapsed, autoEndDeadlineSeconds, isEnding, sessionId, navigate]); 

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 1. Browser Level Block (Refresh, Close Tab, Back Button)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isEnding && (connectionStatus === 'connected' || isListening)) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEnding, connectionStatus, isListening]);

  // 2. In-App Navigation Block (React Router)
  useBlocker((tx) => {
    // If we are already ending, allow navigation
    if (isEnding) {
      tx.retry();
      return;
    }

    // Otherwise, block and show confirmation
    setPendingTx(tx);
    setShowEndInterviewModal(true);
  }, !isEnding && (connectionStatus === 'connected' || isListening));



  // ... (previous imports)

  // ... (inside LiveInterviewPanel component)

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Handle confirming end interview (from Modal)
  const confirmEndInterview = async () => {
    if (!sessionId) return;

    // 1. Set ending flag to disable blocker
    isEndingRef.current = true;
    setIsEnding(true);

    // 2. Stop recording and stop AI playback immediately
    try {
      stopRecording();
    } catch (e) { }
    stopAIPlayback();

    // 3. Close WebSocket to stop STT streams
    try {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    } catch (e) { }
    // 4. Trigger end session API in background (non-blocking)
    // We don't await this so the Feedback Modal appears immediately.
    // The report generation happens on the server.
    endInterviewSession(sessionId, getIntegrityEventsForSession(), readStoredCodeSnapshot(sessionId)).catch(e => {
      console.error("Failed to end session via API:", e);
    });

    // 5. Clear localStorage to prevent replay on back button
    localStorage.removeItem('currentInterviewSession');

    // 6. Close confirmation modal and open Feedback Modal IMMEDIATELY
    setShowEndInterviewModal(false);
    setShowFeedbackModal(true);
    // Ensure all AI states are cleared
    setIsAISpeaking(false);
    setIsListening(false);
    isAIActiveRef.current = false;
    isPlayingRef.current = false;
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    setIsSubmittingFeedback(true);
    console.log("Submitting feedback for session:", sessionId, "Data:", feedbackData);
    try {
      await submitInterviewFeedback(sessionId, feedbackData);
      console.log("Feedback submitted successfully");
    } catch (err) {
      console.error("Failed to submit feedback", err);
    } finally {
      setIsSubmittingFeedback(false);
      finalizeEndInterview(); // Navigate away
    }
  };

  const handleFeedbackSkip = () => {
    finalizeEndInterview();
  };

  const finalizeEndInterview = () => {
    // Ensure localStorage is cleaned up (belt & suspenders)
    localStorage.removeItem('currentInterviewSession');

    // Navigate (or retry pending navigation)
    // Wrap in setTimeout to ensure state update propagates and blocker is removed by useEffect
    setTimeout(() => {
      if (pendingTx) {
        pendingTx.retry();
      } else {
        navigate('/generating-result', {
          state: {
            sessionId,
          }
        });
      }
    }, 100);
  };

  // Handle cancelling end interview
  useEffect(() => {
    if (!showEndInterviewModal && pendingTx) {
      // If modal is closed without confirming (cancelled), we assume we stay.
      // But we can't really "cancel" the tx in this polyfill easily without just ignoring it.
      // The polyfill holds the tx. If we reset pendingTx, we just lose the ability to retry.
      setPendingTx(null);
    }
  }, [showEndInterviewModal]);

  const transcriptEndRef = useRef(null);
  const wsRef = useRef(null);
  const sttInitialized = useRef(false);
  const reconnectTimerRef = useRef(null);
  const keepAliveTimerRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const processedMessageIds = useRef(new Set()); // Track processed messages to prevent duplicates
  const wsInitialized = useRef(false);
  const lastProcessedMessage = useRef(null); // Track last message to prevent immediate duplicates
  const mediaRecorderRef = useRef(null);
  const recordingStartInFlightRef = useRef(false);
  const mediaStreamRef = useRef(null);
  const currentAudioRef = useRef(null);
  const audioQueue = useRef([]);
  const isPlayingRef = useRef(false);
  const isAIActiveRef = useRef(false); // New Ref to track thinking + speaking
  const analyserRef = useRef(null);
  const aiAnalyserRef = useRef(null);
  const lastSentenceIndexRef = useRef(-1);
  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  const pausedRef = useRef(false);
  const aiHoldRef = useRef(false);
  const aiPauseTimerRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  const activeAudioNodesRef = useRef([]);
  const lastBrowserTranscriptRef = useRef({ text: '', timestamp: 0, isFinal: false });
  // Tracks whether the user is actively speaking right now (set true on any STT input, false on VAD silence).
  // Used to delay AI audio playback so speculative audio doesn't start mid-user-speech.
  const isUserActivelySpeakingRef = useRef(false);
  // Ref to playNextChunk so the VAD closure (inside startRecording) can trigger deferred playback.
  const playNextChunkRef = useRef(null);
  const isDecodingRef = useRef(false);
  const integrityEventsRef = useRef(readStoredIntegrityEvents(sessionId));
  const integrityDedupRef = useRef(new Map());
  const lastInternalCopyRef = useRef("");

  const sendIntegrityEventToServer = useCallback((event) => {
    if (!event || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    try {
      wsRef.current.send(JSON.stringify({
        type: 'integrity_event',
        data: event,
      }));
    } catch (error) {
      console.error('Failed to send integrity event', error);
    }
  }, []);

  const getIntegrityEventsForSession = useCallback(() => {
    const byId = new Map();
    [...integrityEventsRef.current, ...readStoredIntegrityEvents(sessionId)].forEach((event) => {
      if (!event?.signal) return;
      const id = event.id || `${event.signal}-${event.timestamp}`;
      byId.set(id, { ...event, id });
    });
    return Array.from(byId.values()).slice(0, 100);
  }, [sessionId]);

  const recordIntegrityEvent = useCallback((signal, details = {}) => {
    if (!sessionId || isEnding) return;
    const rule = INTEGRITY_RULES[signal];
    if (!rule) return;

    const now = Date.now();
    const dedupKey = `${signal}:${details.source || details.reason || 'global'}`;
    const lastAt = integrityDedupRef.current.get(dedupKey) || 0;
    if (now - lastAt < 1500) return;
    integrityDedupRef.current.set(dedupKey, now);

    const event = {
      id: `${signal}-${now}-${Math.random().toString(36).slice(2, 8)}`,
      signal,
      severity: rule.severity,
      message: rule.message,
      timestamp: new Date(now).toISOString(),
      details: {
        ...details,
        path: window.location.pathname,
      },
      event_count: integrityEventsRef.current.length + 1,
    };

    const nextEvents = [event, ...integrityEventsRef.current].slice(0, 100);
    integrityEventsRef.current = nextEvents;
    setIntegrityEvents(nextEvents);
    setVisibleIntegrityEvent(event);
    writeStoredIntegrityEvents(sessionId, nextEvents);
    sendIntegrityEventToServer(event);
  }, [isEnding, sendIntegrityEventToServer, sessionId]);

  useEffect(() => {
    const stored = readStoredIntegrityEvents(sessionId);
    integrityEventsRef.current = stored;
    setIntegrityEvents(stored);
    setVisibleIntegrityEvent(null);
  }, [sessionId]);

  useEffect(() => {
    if (!visibleIntegrityEvent) return undefined;

    const hideTimer = window.setTimeout(() => {
      setVisibleIntegrityEvent(null);
    }, 5000);

    return () => window.clearTimeout(hideTimer);
  }, [visibleIntegrityEvent]);

  useEffect(() => {
    if (!sessionId) return undefined;

    const preventAndRecord = (event, signal, details) => {
      event.preventDefault();
      event.stopPropagation();
      recordIntegrityEvent(signal, details);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        recordIntegrityEvent('tab_switch', { reason: 'document_hidden', source: 'visibilitychange' });
      }
    };

    const handleWindowBlur = () => {
      // Use a short delay + hasFocus() check to avoid false positives.
      // Clicking within the IDE (Monaco) or other internal elements causes the
      // window to briefly lose focus — delay lets it refocus before we decide.
      setTimeout(() => {
        if (!document.hasFocus()) {
          recordIntegrityEvent('tab_switch', { reason: 'window_blur', source: 'blur' });
        }
      }, 300);
    };

    const handleKeyDown = (event) => {
      const key = event.key?.toLowerCase();
      const hasModifier = event.ctrlKey || event.metaKey;
      if (hasModifier && (key === 'c' || key === 'x')) {
        // Handled by handleCopy/handleCut if from editor
        if (!event.target?.closest?.('.monaco-editor')) {
          preventAndRecord(event, 'copy_attempt', { shortcut: `${event.metaKey ? 'meta' : 'ctrl'}+${key}`, source: 'keyboard' });
        }
      } else if (hasModifier && key === 'v') {
        // Handled by handlePaste if from editor
        if (!event.target?.closest?.('.monaco-editor')) {
          preventAndRecord(event, 'paste', { shortcut: `${event.metaKey ? 'meta' : 'ctrl'}+v`, source: 'keyboard' });
        }
      } else if (event.shiftKey && key === 'insert') {
        if (!event.target?.closest?.('.monaco-editor')) {
          preventAndRecord(event, 'paste', { shortcut: 'shift+insert', source: 'keyboard' });
        }
      }
    };

    const handleCopy = (event) => {
      if (event.target?.closest?.('.monaco-editor')) {
        const selection = window.getSelection();
        if (selection) {
          lastInternalCopyRef.current = selection.toString();
        }
        return; // Allow internal copy
      }
      preventAndRecord(event, 'copy_attempt', { source: event.type });
    };

    const handlePaste = (event) => {
      const text = event.clipboardData?.getData('text') || '';
      if (event.target?.closest?.('.monaco-editor')) {
        // If pasting exactly what was last copied internally, allow it
        if (text && text === lastInternalCopyRef.current) {
           return;
        }
      }
      preventAndRecord(event, 'paste', { source: 'clipboard', length: text.length });
    };

    const handleSelectStart = (event) => {
      const target = event.target;
      if (target?.closest?.('[data-question-content="true"]')) {
        preventAndRecord(event, 'selection_attempt', { source: 'question_panel' });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, true);
    window.addEventListener('blur', handleWindowBlur, true);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('copy', handleCopy, true);
    document.addEventListener('cut', handleCopy, true);
    document.addEventListener('paste', handlePaste, true);
    document.addEventListener('selectstart', handleSelectStart, true);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange, true);
      window.removeEventListener('blur', handleWindowBlur, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('copy', handleCopy, true);
      document.removeEventListener('cut', handleCopy, true);
      document.removeEventListener('paste', handlePaste, true);
      document.removeEventListener('selectstart', handleSelectStart, true);
    };
  }, [recordIntegrityEvent, sessionId]);

  const stopAIPlayback = () => {
    audioQueue.current = [];
    isDecodingRef.current = false;
    setCurrentCaption("");
    currentCaptionRef.current = "";
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.src = "";
        currentAudioRef.current.load();
      } catch (e) {
        console.error("Error stopping current audio ref", e);
      }
      currentAudioRef.current = null;
    }

    activeAudioNodesRef.current.forEach(source => {
      try {
        source.stop();
      } catch (e) {
        // ignore stop errors
      }
    });
    activeAudioNodesRef.current = [];
    nextPlayTimeRef.current = 0;
    isPlayingRef.current = false;
    setIsAISpeaking(false);
  };

  // Helper to convert base64 to ArrayBuffer
  const base64ToArrayBuffer = (base64) => {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  // Start recording from microphone and stream raw audio blobs (or text) to the backend
  async function startRecording({ resumeBrowserStt = false } = {}) {
    if (
      mediaRecorderRef.current
      || recordingStartInFlightRef.current
      || mutedRef.current
      || pausedRef.current
      || !navigator.mediaDevices
      || wsRef.current?.readyState !== WebSocket.OPEN
    ) return;

    recordingStartInFlightRef.current = true;
    
    // Declare VAD state variables at function scope so both checkVolume and sttService callbacks share them
    let silenceStart = null;
    let userWasSpeaking = false;
    let vadEnabled = false; // Don't trigger VAD until first speech detected
    let userSpeakingStartTime = 0; // Track when user started speaking (min duration guard)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // The user may mute, pause, or leave while the permission request is pending.
      if (mutedRef.current || pausedRef.current || wsRef.current?.readyState !== WebSocket.OPEN) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      mediaStreamRef.current = stream;

      if (sttSource === 'browser') {
        console.log("[STT] Using Browser-native Speech Recognition");
        sttService.setOnResult((result) => {
          const text = result.isFinal ? result.finalText : result.interimText;
          if (result.isFinal && lastBrowserTranscriptRef.current.flushedByVAD) {
            lastBrowserTranscriptRef.current.flushedByVAD = false;
            return;
          }

         
          if (isPlayingRef.current) {
            return;
          }

         
          if (text && text.trim().length > 0) {
            isUserActivelySpeakingRef.current = true;
            if (!userWasSpeaking) {
              userSpeakingStartTime = Date.now();
            }
            userWasSpeaking = true;
            vadEnabled = true;
            silenceStart = null;
          }

          lastBrowserTranscriptRef.current = {
            text: text || '',
            timestamp: Date.now(),
            isFinal: result.isFinal,
           
            flushedByVAD: false
          };

          const cleanText = normalizeSpeechText(text);

          // ── AI NOT SPEAKING (or just interrupted): forward to backend normally ──
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !mutedRef.current) {
            if (result.isFinal) {
              setTranscript(prev => [...prev, {
                speaker: 'you',
                text: result.finalText,
                timestamp: Date.now(),
                confidence: result.confidence
              }]);
              setTimeout(() => {
                transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 50);
              wsRef.current.send(JSON.stringify({
                type: 'final_transcript',
                text: result.finalText,
                confidence: result.confidence
              }));
            } else {
              wsRef.current.send(JSON.stringify({
                type: 'interim_transcript',
                text: result.interimText
              }));
            }
          }
        });
        sttService.setOnStart(() => setIsListening(true));
        sttService.setOnEnd(() => setIsListening(false));
        if (resumeBrowserStt) {
          sttService.resume();
        } else {
          sttService.start();
        }

        // Dummy object to satisfy cleanup logic
        mediaRecorderRef.current = {
          state: 'recording',
          stop: () => {
            // Keep STT running during interruptions in browser mode
            console.log("[STT] mediaRecorder.stop() called (no-op in browser mode)");
          }
        };
      } else {
	    console.log("[STT] Using Sarvam realtime speech recognition");
	    // Sarvam realtime STT accepts 16 kHz PCM (not MediaRecorder's WebM/Opus chunks).
	    const pcmContext = new (window.AudioContext || window.webkitAudioContext)();
	    const source = pcmContext.createMediaStreamSource(stream);
	    const processor = pcmContext.createScriptProcessor(4096, 1, 1);
	    const silentGain = pcmContext.createGain();
	    silentGain.gain.value = 0;
	    source.connect(processor);
	    processor.connect(silentGain);
	    silentGain.connect(pcmContext.destination);
	    processor.onaudioprocess = (event) => {
	      // Do not feed the interviewer's speaker output back into Sarvam. Its
	      // realtime partials otherwise look like a candidate barge-in and mute
	      // the welcome audio partway through.
	      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || mutedRef.current || isPlayingRef.current || isAIActiveRef.current) return;
	      const input = event.inputBuffer.getChannelData(0);
	      const ratio = pcmContext.sampleRate / 16000;
	      const output = new Int16Array(Math.floor(input.length / ratio));
	      for (let i = 0; i < output.length; i += 1) {
	        const sample = input[Math.min(Math.floor(i * ratio), input.length - 1)];
	        output[i] = Math.max(-1, Math.min(1, sample)) * 0x7fff;
	      }
	      try { wsRef.current.send(output.buffer); } catch (sendErr) { console.error('Failed to send PCM audio chunk', sendErr); }
	    };
	    mediaRecorderRef.current = {
	      state: 'recording',
	      stop: () => { processor.disconnect(); silentGain.disconnect(); source.disconnect(); pcmContext.close(); setIsListening(false); }
	    };
	    setIsListening(true);
      }

      // Set up volume monitoring for barge-in (shared logic)
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      silenceStart = null;
      userWasSpeaking = false;
      vadEnabled = false; // Don't trigger VAD until first speech detected
      userSpeakingStartTime = 0; // Track when user started speaking (min duration guard)

      const checkVolume = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteTimeDomainData(dataArray);

        let maxAmplitude = 0;
        for (let i = 0; i < bufferLength; i++) {
          const amplitude = Math.abs(dataArray[i] - 128);
          if (amplitude > maxAmplitude) {
            maxAmplitude = amplitude;
          }
        }

        // Threshold: 30 amplitude during user turn (was 25) to reduce noise false-positives
        const threshold = isPlayingRef.current ? 45 : 30;
        const isSpeakingNow = maxAmplitude > threshold;

        // 1. Visual state only during AI playback (Barge-In visuals)
        if (isPlayingRef.current || isAIActiveRef.current) {
          // Reset VAD state while AI is active
          userWasSpeaking = false;
          silenceStart = null;
          vadEnabled = false;
          setIsUserSpeaking(isSpeakingNow);
        } else {
          // 2. VAD Logic (if it's the User's turn to speak)
          if (isSpeakingNow) {
            setIsUserSpeaking(true);
            isUserActivelySpeakingRef.current = true; // user is mid-speech
            if (!userWasSpeaking) {
              userSpeakingStartTime = Date.now(); // record when speech began
            }
            userWasSpeaking = true;
            vadEnabled = true; // User started speaking, VAD is now live
            silenceStart = null;
          } else {
            setIsUserSpeaking(false);
            if (userWasSpeaking && vadEnabled) {
              if (!silenceStart) {
                silenceStart = Date.now();
              } else if (Date.now() - silenceStart >= VAD_SILENCE_MS) {
                // Minimum speaking duration guard: ignore if total speech was < 400ms
                // (background noise spike that briefly exceeded the threshold)
                const spokenDuration = silenceStart - userSpeakingStartTime;
                if (spokenDuration < 400) {
                  console.log(`[VAD] Ignoring noise spike — only spoke for ${spokenDuration}ms (< 400ms min).`);
                  userWasSpeaking = false;
                  silenceStart = null;
                  vadEnabled = false;
                } else if (sttSource !== 'browser') {
                  // Sarvam sends its own final transcript through the backend.
                  // The browser VAD has no Sarvam transcript to flush, so sending
                  // user_finished_speaking here produces an empty turn and can
                  // incorrectly block subsequent microphone PCM chunks.
                  userWasSpeaking = false;
                  silenceStart = null;
                  vadEnabled = false;
                } else {
                  const transcriptText = normalizeSpeechText(lastBrowserTranscriptRef.current.text || '');
                  if (isFillerSpeech(transcriptText)) {
                    console.log("[VAD] 2s silence after filler — not sending user_finished_speaking.");
                    userWasSpeaking = false;
                    silenceStart = null;
                  } else {
                    // User has been silent for 2s after speaking -> They are finished!
                    console.log("[VAD] 2s silence confirmed — sending user_finished_speaking.");
                    // STT remains OPEN (per user request) to allow faster turnaround
                    isUserActivelySpeakingRef.current = false;
                    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                      // Bug #1 fix: only include a pendingText flush when the last captured
                      // result was still interim (i.e. STT hasn't finalised it yet). If the
                      // ref is already final, the text was already sent via final_transcript
                      // and we send an empty pendingText so the backend knows to use whatever
                      // it already received rather than an empty answer.
                      let pendingText = "";
                      const ref = lastBrowserTranscriptRef.current;
                      if (!ref.isFinal && ref.text && ref.text.trim().length > 0) {
                        pendingText = ref.text;
                        lastBrowserTranscriptRef.current = { ...ref, isFinal: true, flushedByVAD: true };
                      }
                      wsRef.current.send(JSON.stringify({
                        type: 'user_finished_speaking',
                        text: pendingText
                      }));
                    }
                    userWasSpeaking = false;
                    silenceStart = null;
                    // Mark AI as active to prevent duplicate triggers while waiting for backend
                    isAIActiveRef.current = true;
                    // If speculative audio already arrived while user was speaking, start it now
                    if (audioQueue.current.length > 0 && !isPlayingRef.current && playNextChunkRef.current) {
                      console.log('[VAD] Deferred speculative audio — starting playback now.');
                      playNextChunkRef.current();
                    }
                  } // end else (not filler)
                } // end else (spoke >= 400ms)
              } // end else if (silence >= VAD_SILENCE_MS)
            } // end if (userWasSpeaking && vadEnabled)
          } // end else (not speaking)
        }
      };

      // Use a high-frequency interval (5ms) instead of requestAnimationFrame (16ms)
      // for near-instant detection regardless of UI frame rate.
      const volumeInterval = setInterval(checkVolume, 5);
      mediaRecorderRef.current.onvolumeinterval = volumeInterval; // Store for cleanup
    } catch (err) {
      console.error('Failed to start media recorder', err);
    } finally {
      recordingStartInFlightRef.current = false;
    }
  }

  function handleLocalInterruption() {
    // Use isPlayingRef instead of state for immediate check
    if (!isPlayingRef.current) return;

    console.log("[BargeIn] Stopping AI speech instantly...");

    stopAIPlayback();
    isAIActiveRef.current = false;

    // Notify backend to truncate transcript and stop synthesis
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'user_interruption',
        sentence_index: lastSentenceIndexRef.current
      }));
    }
  }

  function stopRecording() {
    try {
      // Bug #9 fix: capture the interval ID before nulling the ref so it can
      // always be cleared even if an error is thrown mid-cleanup.
      const volumeInterval = mediaRecorderRef.current?.onvolumeinterval ?? null;
      if (volumeInterval !== null) {
        clearInterval(volumeInterval);
      }
      if (mediaRecorderRef.current) {
        if (sttSource === 'browser') {
          sttService.stop();
        } else {
          if (mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
        }
      }
      if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach((t) => t.stop()); mediaStreamRef.current = null; }
      mediaRecorderRef.current = null;
      // DO NOT close audioContextRef here because it is shared with the AI audio playback visualizer.
      // Simply let the context run (it takes minimal resources when inactive) or close it only on unmount.
    } catch (err) {
      console.error('Failed to stop recording', err);
    }
  }



  useEffect(() => {
    if (!sessionId) {
      localStorage.removeItem('currentInterviewSession');
      navigate('/interview-setup');
      return;
    }

    // SESSION GUARD: Check session status before connecting
    // If session is already completed/inactive, redirect out — prevents replay
    getSession(sessionId).then(session => {
      if (session && (session.status === 'COMPLETED' || session.status === 'completed' || session.is_active === false || session.is_active?.Bool === false)) {
        console.warn(`[SessionGuard] Session ${sessionId} is already completed/inactive. Redirecting.`);
        localStorage.removeItem('currentInterviewSession');
        navigate('/interview-setup', { state: { error: 'This session has already ended. Please start a new interview.' } });
        return;
      }
      if (session && (session.status === 'PAUSED' || session.status === 'paused')) {
        setIsPaused(true);
        pausedRef.current = true;
      }

      const persistedStart = Number(session?.interview_state?.start_time);
      if (Number.isFinite(persistedStart) && persistedStart > 0) {
        setSessionStartUnix(Math.floor(persistedStart));
      }

      if (session?.active_question) {
        setCurrentQuestion(session.active_question);
        setShowIDE(true);
        setInterviewStage('question');
      }

      if (Array.isArray(session?.transcript)) {
        setTranscript(session.transcript.map((entry) => ({
          ...entry,
          speaker: entry.speaker === 'user' ? 'you' : entry.speaker,
          timestamp: typeof entry.timestamp === 'number' ? entry.timestamp * 1000 : entry.timestamp,
          isFinal: true,
        })));
      }
    }).catch(err => {
      // 404 is expected if the route is incorrect or session is not yet fully committed
      if (err.status === 404 || err.response?.status === 404) {
        console.warn(`[SessionCheck] Session ${sessionId} not found on server yet. This might be a prefix issue or delay.`);
      } else {
        console.warn("Session status check failed:", err.message || err);
      }
    });


    // Prevent multiple WebSocket connections
    if (wsInitialized.current || wsRef.current) {
      return;
    }

    wsInitialized.current = true;
    shouldReconnectRef.current = true;

    // ...existing code removed (media recorder moved to component scope)

    // Create WebSocket connection
    const apiBase = getApiBaseUrl();
    const wsBase = apiBase.replace(/^http/, 'ws');
    const wsUrl = `${wsBase}/ws?sessionId=${sessionId}&interviewerName=${encodeURIComponent(interviewer.name)}&interviewerRole=${encodeURIComponent(interviewer.role)}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    setConnectionStatus('connecting');

    // Warm up AudioContext early to avoid lag
    const warmUpAudio = async () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume().catch(e => console.log("Audio warm-up note: Waiting for interaction."));
        }
        console.log("[Audio] Engine warmed up early.");
      }
    };
    warmUpAudio();

    ws.onopen = () => {
      setConnectionStatus('connected');
      setIsConnecting(false); // Remove loading state from interviewer avatar

      // Send start message to trigger the welcome greeting
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'start',
          sessionId: sessionId,
          // A reconnect must restore the existing turn rather than replaying the intro.
          is_resume: isResume || connectionAttempt > 0,
        }));
        getIntegrityEventsForSession().reverse().forEach((event) => {
          ws.send(JSON.stringify({ type: 'integrity_event', data: event }));
        });
      }

      // Start listening for user speech after a delay
      // Use a ref to check mute status to avoid dependency issues
      setTimeout(() => {
        if (!mutedRef.current && !pausedRef.current) {
          startRecording();
        }
      }, 50);

      // Browsers cannot send WebSocket control-frame pings. An inexpensive
      // application message keeps reverse proxies/load balancers from dropping
      // an otherwise idle interview connection during a long thinking pause.
      if (keepAliveTimerRef.current) clearInterval(keepAliveTimerRef.current);
      keepAliveTimerRef.current = window.setInterval(() => {
        if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ type: 'keepalive' }));
          } catch (error) {
            console.warn('[WebSocket] Keepalive failed', error);
          }
        }
      }, 25_000);
    };

    const playNextChunk = async () => {
      if (isDecodingRef.current) {
        console.log("[Audio] Already decoding queue, skipping concurrent trigger.");
        return;
      }

      if (audioQueue.current.length === 0) {
        // Debounce AI inactive state to prevent VAD from triggering during stream gaps
        setTimeout(() => {
          if (audioQueue.current.length === 0 && activeAudioNodesRef.current.length === 0) {
            isPlayingRef.current = false;
            isAIActiveRef.current = false;
            setIsAISpeaking(false);
            setCurrentCaption("");
            currentCaptionRef.current = "";
            console.log("[Audio] Playback finished — starting STT for user turn.");
            // STT ON: AI is done speaking, user's turn to respond
            if (sttSource === 'browser' && !mutedRef.current && !pausedRef.current) {
              sttService.resume();
            }
          } else if (audioQueue.current.length > 0) {
            // Chunks arrived during the debounce gap.
            console.log(`[Audio] Debounce found ${audioQueue.current.length} queued chunks — resuming playback.`);
            playNextChunk();
          }
        }, 1200); // 1.2s buffer
        return;
      }

      isDecodingRef.current = true;
      setIsAISpeaking(true);
      isPlayingRef.current = true;

      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        while (audioQueue.current.length > 0) {
          const { base64: nextBase64, isPCM, text: nextText } = audioQueue.current.shift();
          
          if (nextText && nextText !== "-1") {
            setCurrentCaption(nextText);
            currentCaptionRef.current = nextText;
          } else {
            setCurrentCaption("");
            currentCaptionRef.current = "";
          }

          const audioData = base64ToArrayBuffer(nextBase64);
          let audioBuffer;

          if (isPCM) {
            // Handle Raw PCM (Linear16)
            const int16Array = new Int16Array(audioData);
            const float32Array = new Float32Array(int16Array.length);
            for (let i = 0; i < int16Array.length; i++) {
              float32Array[i] = int16Array[i] / 32768.0;
            }
            audioBuffer = audioContextRef.current.createBuffer(1, float32Array.length, 24000);
            audioBuffer.getChannelData(0).set(float32Array);
            console.log(`[Audio] Playing PCM chunk: ${float32Array.length} samples at 24kHz`);
          } else {
            // Handle Compressed formats (MP3/WAV)
            console.log(`[Audio] Decoding compressed chunk: ${audioData.byteLength} bytes`);

            // Add a safety timeout and backward-compatible Promise wrapper
            audioBuffer = await Promise.race([
              new Promise((resolve, reject) => {
                try {
                  const p = audioContextRef.current.decodeAudioData(
                    audioData,
                    (buf) => resolve(buf),
                    (err) => reject(err || new Error('decodeAudioData callback error'))
                  );
                  if (p && typeof p.catch === 'function') {
                    p.then(resolve).catch(reject);
                  }
                } catch (e) {
                  reject(e);
                }
              }),
              new Promise((_, reject) => setTimeout(() => reject(new Error("decodeAudioData timeout")), 3000))
            ]);
          }

          const source = audioContextRef.current.createBufferSource();
          source.buffer = audioBuffer;
          source.playbackRate.value = 1.0;

          ensureGainNode();
          source.connect(gainNodeRef.current);

          const currentTime = audioContextRef.current.currentTime;
          if (nextPlayTimeRef.current < currentTime + 0.05) {
            nextPlayTimeRef.current = currentTime + 0.05;
          }

          const startTime = nextPlayTimeRef.current;
          source.start(startTime);

          if (!isPCM) {
            console.log(`[Audio] Playing compressed chunk: duration ${audioBuffer.duration.toFixed(2)}s at ${startTime.toFixed(2)}`);
          }

          const duration = audioBuffer.duration;
          nextPlayTimeRef.current += duration;

          activeAudioNodesRef.current.push(source);

          source.onended = () => {
            activeAudioNodesRef.current = activeAudioNodesRef.current.filter(n => n !== source);
            
            // If all sources finished playing and queue is empty, handle transition
            if (activeAudioNodesRef.current.length === 0 && audioQueue.current.length === 0) {
              isPlayingRef.current = false;
              isAIActiveRef.current = false;
              setIsAISpeaking(false);
              setCurrentCaption("");
              currentCaptionRef.current = "";
              console.log("[Audio] Last source ended — starting STT for user turn.");
              if (sttSource === 'browser' && !mutedRef.current && !pausedRef.current) {
                sttService.resume();
              }
            }
          };
        }
      } catch (err) {
        console.error("Failed to play audio chunk", err);
      } finally {
        isDecodingRef.current = false;
      }
    };

    // Expose playNextChunk so the VAD closure (inside startRecording) can trigger deferred playback
    playNextChunkRef.current = playNextChunk;

    ws.onmessage = async (event) => {
      if (isEnding) return;
      try {
        const data = JSON.parse(event.data);
        const now = Date.now();

        // Handle session expired/invalid from backend guard
        if (data.type === 'session_expired') {
          console.warn('[SessionGuard] Server rejected: session expired/completed.');
          localStorage.removeItem('currentInterviewSession');
          navigate('/interview-setup', { state: { error: data.error || 'Session expired. Please start a new interview.' } });
          return;
        }

        // Handle session timeout — duration exceeded, auto-end interview
        if (data.type === 'session_timeout') {
          console.warn('[SessionTimeout] Interview duration exceeded. Auto-ending.');
          isEndingRef.current = true;
          setIsEnding(true);
          try { stopRecording(); } catch (e) { }

          // Call EndSession to generate the report
          endInterviewSession(sessionId, getIntegrityEventsForSession(), readStoredCodeSnapshot(sessionId)).catch(e => {
            console.error("Failed to end session on timeout:", e);
          });

          localStorage.removeItem('currentInterviewSession');

          // Navigate to report generation page
          navigate('/generating-result', {
            state: { sessionId, autoEnded: true }
          });
          return;
        }

        // Handle streaming response chunks
        if (data.type === 'ai_response_chunk') {
          // Append text to last message if it's from AI, or create new
          setTranscript(prev => {
            const last = prev[prev.length - 1];
            if (last && last.speaker === 'interviewer' && !last.isFinal) {
              // Update existing
              const updated = [...prev];
              updated[prev.length - 1] = {
                ...last,
                text: last.text + data.text
              };
              return updated;
            } else {
              // New message
              return [...prev, { speaker: 'interviewer', text: data.text, timestamp: now, isFinal: false }];
            }
          });

          // Scroll
          setTimeout(() => {
            transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 50);
        }

        else if (data.type === 'audio_chunk') {
          if (data.audioBase64) {
            const chunk = {
              base64: data.audioBase64,
              isPCM: data.is_pcm,
              text: data.text
            };

            if (data.text === "-1" || !data.text) {
              // Filler audio — push to FRONT of queue for immediate playback
              audioQueue.current.unshift(chunk);
            } else {
              // Real response audio
              audioQueue.current.push(chunk);
            }

            if (!isPlayingRef.current) {
              isAIActiveRef.current = true; // Mark as active immediately
              // STT OFF: stop listening while AI speaks to avoid echo/noise
              if (sttSource === 'browser') {
                sttService.stop();
              }
              if (isUserActivelySpeakingRef.current) {
                // User is still mid-sentence; queue the chunk but defer playback.
                // VAD will trigger playNextChunk once the user finishes speaking.
                // Safety fallback: if VAD doesn't fire within 1.5s, force playback anyway
                // (guards against the ref getting stuck due to background noise).
                console.log('[Audio] Deferring AI audio playback — user is still speaking.');
                setTimeout(() => {
                  if (!isPlayingRef.current && audioQueue.current.length > 0) {
                    console.log('[Audio] Deferred safety timeout fired — forcing playback.');
                    isUserActivelySpeakingRef.current = false;
                    playNextChunk();
                  }
                }, 1000);
              } else {
                playNextChunk();
              }
            } else {
              // Proactively process queue to decode and schedule new chunk
              playNextChunk();
            }
          }
        }

        else if (data.type === 'interruption') {
          // Stop playback immediately
          stopAIPlayback();
        }

        else if (data.type === 'transcript_interim') {
          // Instant interruption using actual Google STT speech decoding (ignores pure noise)
          const text = data.text || "";
          console.log(`[STT: third-party] Interim: ${text}`);
          setIsUserSpeaking(true);

          const fillers = ['ok', 'okay', 'oh', 'ohh', 'got it', 'i see', 'um', 'uh', 'ah', 'right', 'hmm'];
          const cleanText = text.toLowerCase().replace(/[.,!]/g, '').trim();
          const aiText = normalizeSpeechText(currentCaptionRef.current);
          const isEcho = aiText && (aiText.includes(cleanText) || cleanText.includes(aiText));

          const minLength = isPlayingRef.current ? 5 : 2;
          const isMeaningful = cleanText.length >= minLength && !isFillerSpeech(cleanText) && !isEcho;

          if (isPlayingRef.current && cleanText.length >= 2 && !isEcho) {
            if (aiHoldRef.current) {
              if (isMeaningful) {
                console.log("[BargeIn: third-party] Meaningful speech continues → cancelling AI.");
                confirmLocalInterruption();
              }
            } else {
              console.log("[BargeIn: third-party] Speech detected → pausing AI.");
              pauseAIPlaybackMomentarily();
            }
          }
        }

        else if (data.type === 'transcript') {
          // User transcript (final)
          const text = data.text || "";
          setTranscript(prev => [...prev, {
            speaker: 'you',
            text: text,
            timestamp: now,
            confidence: data.confidence
          }]);
          // Set active as soon as user finishing speaking, waiting for AI
          isAIActiveRef.current = true;
          setIsUserSpeaking(false);

          // Scroll
          setTimeout(() => {
            transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 50);

          // Meaningful interrupt logic if it wasn't caught by interim (failsafe)
          const fillers = ['ok', 'okay', 'oh', 'ohh', 'got it', 'i see', 'um', 'uh', 'ah', 'right', 'hmm'];
          const cleanText = text.toLowerCase().replace(/[.,!]/g, '').trim();
          const isMeaningful = cleanText.length > 5 && !fillers.includes(cleanText);

          if (isMeaningful && isPlayingRef.current) {
            console.log("[BargeIn] Meaningful final speech detected, interrupting AI...");
            handleLocalInterruption();
          } else if (!isMeaningful) {
            console.log("[BargeIn] Short/Filler detected, allowing AI to continue speaking.");
          }
        }

        // Handle Legacy/Control types
        else if (data.type === 'system') {
          if (data.text === 'reset_audio') {
            console.log("Received system command: reset_audio. Restarting media recorder...");
            stopRecording();
            // Wait briefly for stop to complete then restart
            setTimeout(() => {
              startRecording();
            }, 500);
          }
        } else if (data.type === 'control') {
          if (data.action === 'enable_editor' || data.action === 'open_ide') {
            setShowIDE(true);
            setWorkspaceTab('both');
          }
        } else if (data.type === 'turn_ignored') {
          isAIActiveRef.current = false;
          setIsAISpeaking(false);
          if (sttSource === 'browser' && !mutedRef.current && !pausedRef.current) {
            sttService.resume();
          }
        } else if (data.type === 'ai_response') {
          // Full response fallback (e.g. welcome message)
          setTranscript(prev => [...prev, { speaker: 'interviewer', text: data.text || data.message, timestamp: now, isFinal: true }]);
          if (data.audioBase64) {
            // STT OFF: stop listening while AI speaks
            if (sttSource === 'browser') {
              sttService.stop();
            }
            audioQueue.current.push({ base64: data.audioBase64, isPCM: false, text: data.text || data.message });
            if (!isPlayingRef.current) {
              playNextChunk();
            }
          } else {
            // Failsafe: if this was a fast or text-only turn completion and no audio is playing/queued,
            // unlock the VAD state so the candidate can speak again.
            if (!isPlayingRef.current && audioQueue.current.length === 0) {
              isAIActiveRef.current = false;
              setIsAISpeaking(false);
              if (sttSource === 'browser' && !mutedRef.current && !pausedRef.current) {
                sttService.resume();
              }
            }
          }

          // Handle Coding Problem if present
          if (data.codingProblem) {
            console.log("Received coding problem:", data.codingProblem);
            const cp = data.codingProblem;
            const questionData = {
              title: cp.title || cp.Title || "Coding Challenge",
              description: cp.description || cp.Description || cp.problem_statement,
              difficulty: cp.difficulty || cp.Difficulty || 'Medium',
              examples: cp.examples || cp.Examples || [],
              constraints: cp.constraints || cp.Constraints || [],
              hints: cp.hints || cp.Hints || [],
              id: cp.id || cp.ID || cp.question_id,
              test_cases: cp.test_cases || cp.TestCases || cp.testCases || [],
              boilerplate: cp.boilerplate || cp.Boilerplate || "",
              boilerplates: cp.boilerplates || cp.Boilerplates || {}
            };
            setCurrentQuestion(prev => {
              if (!prev || prev.id !== questionData.id) return questionData; // If new question, replace entirely
              return {
                ...prev,
                ...questionData,
                examples: (questionData.examples && questionData.examples.length > 0) ? questionData.examples : (prev.examples || []),
                test_cases: (questionData.test_cases && questionData.test_cases.length > 0) ? questionData.test_cases : (prev.test_cases || []),
                constraints: (questionData.constraints && questionData.constraints.length > 0) ? questionData.constraints : (prev.constraints || []),
                hints: (questionData.hints && questionData.hints.length > 0) ? questionData.hints : (prev.hints || [])
              };
            });
            setExecutionResult(null); // Reset execution result for new question
            setShowIDE(true);
            setInterviewStage('question');
          }
          // Handle Design Problem if present
          if (data.designProblem) {
            const dp = data.designProblem;
            const questionData = {
              kind: 'design',
              title: dp.title,
              description: dp.description,
              requirements: dp.requirements || [],
              constraints: dp.constraints || [],
              difficulty: 'Hard'
            };
            setCurrentQuestion(questionData);
            setExecutionResult(null); // Reset execution result for new question
            setShowIDE(true);
            setInterviewStage('question');
          }
        }
        else if (data.type === 'question' || data.type === 'NEXT_QUESTION') {
          if (data.codingProblem) {
            const cp = data.codingProblem;
            const questionData = {
              title: cp.title || cp.Title || "Coding Challenge",
              description: cp.description || cp.Description || cp.problem_statement,
              difficulty: cp.difficulty || cp.Difficulty || 'Medium',
              examples: cp.examples || cp.Examples || [],
              constraints: cp.constraints || cp.Constraints || [],
              hints: cp.hints || cp.Hints || [],
              id: cp.id || cp.ID || cp.question_id,
              test_cases: cp.test_cases || cp.TestCases || cp.testCases || [],
              boilerplate: cp.boilerplate || cp.Boilerplate || "",
              boilerplates: cp.boilerplates || cp.Boilerplates || {}
            };
            setCurrentQuestion(prev => {
              if (!prev || prev.id !== questionData.id) return questionData; // If new question, replace entirely
              return {
                ...prev,
                ...questionData,
                examples: (questionData.examples && questionData.examples.length > 0) ? questionData.examples : (prev.examples || []),
                test_cases: (questionData.test_cases && questionData.test_cases.length > 0) ? questionData.test_cases : (prev.test_cases || []),
                constraints: (questionData.constraints && questionData.constraints.length > 0) ? questionData.constraints : (prev.constraints || []),
                hints: (questionData.hints && questionData.hints.length > 0) ? questionData.hints : (prev.hints || [])
              };
            });
            setExecutionResult(null); // Reset execution result for new question
            setShowIDE(true);
            setInterviewStage('question');
          } else {
            // Legacy/fallback handle question context ...
            const questionData = {
              question: data.content || data.question,
              title: data.content || data.question,
              difficulty: data.difficulty || 'Medium',
              explanation: data.explanation,
              hints: data.hints || [],
              examples: data.examples || [],
              constraints: data.constraints || [],
              boilerplates: data.boilerplates || {},
              test_cases: data.test_cases || []
            };
            // A NEXT_QUESTION event replaces the previous problem even if a
            // legacy payload happens to reuse the same title. Merging here
            // preserved the old description when the new payload was partial.
            setCurrentQuestion(questionData);
            setExecutionResult(null); // Reset execution result for new question
            if (!showIDE) setShowIDE(true);
            setInterviewStage('question');
          }
        }
        else if (data.type === 'execution_result') {
          setExecutionResult(data.executionResult);
          setIsExecuting(false);
        }

      } catch (err) {
        console.error('Failed to parse WS message', err);
      }
    };

    ws.onclose = () => {
      if (keepAliveTimerRef.current) {
        clearInterval(keepAliveTimerRef.current);
        keepAliveTimerRef.current = null;
      }
      if (wsRef.current === ws) wsRef.current = null;
      wsInitialized.current = false;
      setConnectionStatus('disconnected');

      // An idle proxy close is only observable once the candidate speaks again.
      // Reconnect proactively and resume the same server-side session instead
      // of leaving a page that appears to be listening but cannot send audio.
      if (shouldReconnectRef.current && !isEndingRef.current) {
        try { stopRecording(); } catch (e) { }
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          setConnectionAttempt((attempt) => attempt + 1);
        }, 1_000);
      }
    };

    ws.onerror = (err) => {
      console.error('WebSocket error', err);
      setConnectionStatus('error');
    };

    return () => {
      // Cleanup
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (keepAliveTimerRef.current) {
        clearInterval(keepAliveTimerRef.current);
        keepAliveTimerRef.current = null;
      }
      wsInitialized.current = false;
      // stop any recording and playback
      try { stopRecording(); } catch (e) { }
      try { if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; } } catch (e) { }
      if (wsRef.current) {
        wsRef.current.close();
      }
      wsRef.current = null;
      processedMessageIds.current.clear();
      lastProcessedMessage.current = null;
    };
  }, [sessionId, navigate, connectionAttempt]);

  // Keep microphone capture aligned with the requested mute/pause state.
  useEffect(() => {
    if (connectionStatus !== 'connected') return;

    if (isMuted || isPaused) {
      stopRecording();
      setIsListening(false);
      return;
    }

    const restartTimer = window.setTimeout(() => {
      startRecording({ resumeBrowserStt: true });
    }, 150);

    return () => window.clearTimeout(restartTimer);
  }, [isMuted, isPaused, connectionStatus]);


  const handleEndInterview = () => {
    setShowEndInterviewModal(true);
  };


  const codeUpdateTimeoutRef = useRef(null);

  const handleCodeSubmit = (code, language) => {
    // Send code submission via WebSocket immediately
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'code_update',
        code,
        language,
        timestamp: Date.now()
      }));
    }
  };

  const handleCodeChange = (code, language) => {
    // Debounce code updates to avoid flooding WebSocket
    if (codeUpdateTimeoutRef.current) {
      clearTimeout(codeUpdateTimeoutRef.current);
    }

    codeUpdateTimeoutRef.current = setTimeout(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'code_update',
          code,
          language,
          timestamp: Date.now()
        }));
      }
    }, 500); // Send update every 500ms of inactivity
  };

  const [executionResult, setExecutionResult] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);

  const handleCodeRun = async (code, language, input) => {
    setIsExecuting(true);
    setExecutionResult(null); // Clear previous result

    // If we have a question ID, use the new structured execution endpoint
    if (currentQuestion?.id) {
      try {
        const result = await executeInterviewCode({
          question_id: currentQuestion.id,
          code,
          language,
          session_id: sessionId
        });
        setExecutionResult(result);
        setIsExecuting(false);
        return;
      } catch (err) {
        console.error("Structured execution failed, falling back:", err);
        // Fallback to old method or show error
      }
    }

    // Default: use WebSocket for custom input or non-DB questions
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'execution_request',
        code,
        language,
        input,
        timestamp: Date.now()
      }));
    } else {
      setIsExecuting(false);
      console.error("WebSocket not connected");
    }
  };

  const handleWhiteboardUpdate = (data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const payload = typeof data === 'string' ? { state: data } : data;
      wsRef.current.send(JSON.stringify({
        type: 'whiteboard_update',
        data: payload,
        timestamp: Date.now()
      }));
    }
  };

  const handleToggleTranscript = () => {
    setShowTranscript(!showTranscript);
  };

  const handleToggleMute = () => {
    const newMutedState = !isMuted;
    mutedRef.current = newMutedState;
    setIsMuted(newMutedState);
  };

  const handleToggleVideo = () => {
    setIsVideoHidden(!isVideoHidden);
  };

  const handleResume = () => {
    setIsPaused(false);
    pausedRef.current = false;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'resume' }));
    }
  };
  const agentState = isAISpeaking ? 'SPEAKING' : (isListening && !isMuted) ? 'LISTENING' : 'WAITING';
  const lastAiMessage = transcript.filter(t => t.speaker === 'interviewer').slice(-1)[0]?.text || "";

  const ThemeModeToggle = () => (
    <div className="flex items-center rounded-full border border-border bg-muted/60 p-0.5" role="group" aria-label="Color mode">
      <button
        type="button"
        onClick={() => setColorMode('light')}
        aria-pressed={!isDarkMode}
        title="Light mode"
        className={`h-6 w-6 rounded-full p-0 flex items-center justify-center transition-colors ${!isDarkMode ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <Icon name="Sun" size={12} />
      </button>
      <button
        type="button"
        onClick={() => setColorMode('dark')}
        aria-pressed={isDarkMode}
        title="Dark mode"
        className={`h-6 w-6 rounded-full p-0 flex items-center justify-center transition-colors ${isDarkMode ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <Icon name="Moon" size={12} />
      </button>
    </div>
  );

  return (
    <div className="h-screen bg-background text-foreground flex flex-col overflow-hidden relative font-sans">
      {visibleIntegrityEvent && (
        <div className="absolute top-14 left-1/2 z-[120] w-[min(92vw,520px)] -translate-x-1/2 rounded-xl border border-red-200 bg-white/95 px-4 py-3 text-red-700 shadow-2xl shadow-red-500/10 backdrop-blur-md dark:border-red-500/30 dark:bg-[#17181e]/95 dark:text-red-300">
          <div className="flex items-start gap-3">
            <Icon name="ShieldAlert" size={17} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-black tracking-widest">Integrity event recorded</p>
              <p className="mt-0.5 text-[11px] font-semibold leading-relaxed">
                {visibleIntegrityEvent.message}. Score penalty may apply and this will appear in your report.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setVisibleIntegrityEvent(null)}
              className="ml-2 shrink-0 rounded-full p-1 opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Dismiss alert"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}
      {!showIDE ? (
        /* --- ENTRY SCREEN (Image 1) --- */
        <>
          {/* Top Header */}
          <div className="absolute top-0 left-0 w-full h-12 px-5 flex items-center justify-between z-50">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-foreground text-lg tracking-tight">prepxai</span>
              </div>
            </div>

            {/* Center Timer */}
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-card rounded-full shadow-sm border border-border">
                <Icon name="Timer" size={14} className="text-primary" />
                <span className="text-xs font-bold text-foreground font-mono">
                  {formatTime(timeElapsed)}
                </span>
              </div>
              {showAutoEndCountdown && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 rounded-full shadow-sm border border-red-500/30 text-red-600 dark:text-red-400" role="status" aria-live="polite">
                  <Icon name="Clock3" size={14} />
                  <span className="text-[10px] font-black uppercase tracking-wide">Auto-ends in</span>
                  <span className="text-xs font-bold font-mono">{formatTime(autoEndRemainingSeconds)}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => {
                    setShowIssueReportModal(true);
                    setShowReportHint(false);
                  }}
                  className="flex items-center justify-center rounded-full border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 px-2.5 py-1.5 text-rose-500 transition-colors shadow-sm active:scale-95"
                  aria-label="Report Issue"
                  title="Report Issue"
                >
                  <Icon name="Flag" size={13} className="text-rose-500" />
                </button>
                {showReportHint && (
                  <div className="absolute top-10 right-0 z-50 w-64 bg-slate-900/95 backdrop-blur-md text-white p-3 rounded-2xl shadow-2xl border border-slate-700/80 flex items-start gap-2.5 animate-fade-in-up">
                    <div className="p-1.5 bg-rose-500/20 text-rose-400 rounded-xl shrink-0 mt-0.5">
                      <Icon name="Flag" size={14} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-white text-xs tracking-tight">Facing any issue?</p>
                      <p className="text-[11px] font-normal text-slate-300 mt-0.5 leading-snug">
                        Report voice problems, AI hallucinations, or glitches anytime here.
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowReportHint(false);
                      }}
                      className="text-slate-400 hover:text-white p-0.5 rounded-full hover:bg-slate-800 transition-colors"
                    >
                      <Icon name="X" size={13} />
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={handleToggleTranscript}
                className={`flex items-center justify-center rounded-full border px-2.5 py-1.5 transition-colors ${showTranscript ? 'border-primary/20 bg-primary/10 text-primary' : 'border-border bg-card text-slate-400 hover:text-foreground'}`}
                aria-label="Toggle captions"
                title="Toggle captions"
              >
                <Icon name="Subtitles" size={14} />
                <span className="ml-1.5 text-[10px] font-black tracking-widest uppercase">CC</span>
              </button>
              <ThemeModeToggle />
              <button
                onClick={handleEndInterview}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-[#EF4444] hover:bg-red-600 text-white rounded-full font-bold text-xs transition-all shadow-md active:scale-95"
              >
                <Icon name="ChevronUp" size={14} /> Leave
              </button>
            </div>
          </div>

          {/* Center Content */}
          <div className="flex-1 flex flex-col items-center justify-center relative w-full mt-10">
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none z-0" />

            {/* Avatar Container */}
            <div className="relative mb-6 z-10 animate-fade-in-up">
              <div className={`relative w-72 h-72 md:w-80 md:h-80 rounded-full bg-gradient-to-br from-[#E2B091] to-[#C18165] flex items-center justify-center border-4 ${isAISpeaking && !isConnecting ? 'border-emerald-400 dark:border-primary shadow-[0_0_15px_rgba(16,229,143,0.25)]' : 'border-card'} overflow-visible transition-all duration-300`}>
                <style dangerouslySetInnerHTML={{
                  __html: `
                    @keyframes teams-ripple {
                      0% { transform: scale(1); opacity: 0.85; }
                      50% { opacity: 0.45; }
                      100% { transform: scale(1.08); opacity: 0; }
                    }
                    .animate-teams-ripple {
                      animation: teams-ripple 2s cubic-bezier(0.16, 1, 0.3, 1) infinite;
                    }
                  `
                }} />

                {/* Connecting/Loading animation */}
                {isConnecting && (
                  <div className="absolute inset-0 -m-4 pointer-events-none z-20">
                    <div className="absolute inset-0 rounded-full border-4 border-primary/30 border-t-primary animate-spin" style={{ animationDuration: '1.5s' }} />
                    <div className="absolute inset-2 rounded-full border-4 border-transparent border-b-primary/50 animate-spin" style={{ animationDuration: '2.5s', animationDirection: 'reverse' }} />
                  </div>
                )}

                {/* MS Teams-style Staggered Rippling Rings */}
                {isAISpeaking && !isConnecting && (
                  <>
                    {/* Ring 1 */}
                    <div className="absolute -inset-[1px] rounded-full border border-emerald-400/80 dark:border-primary/80 animate-teams-ripple z-[-1]" style={{ animationDelay: '0s', animationDuration: '2s' }} />
                    {/* Ring 2 */}
                    <div className="absolute -inset-[1px] rounded-full border border-emerald-400/80 dark:border-primary/80 animate-teams-ripple z-[-1]" style={{ animationDelay: '0.66s', animationDuration: '2s' }} />
                    {/* Ring 3 */}
                    <div className="absolute -inset-[1px] rounded-full border border-emerald-400/80 dark:border-primary/80 animate-teams-ripple z-[-1]" style={{ animationDelay: '1.33s', animationDuration: '2s' }} />
                    
                    {/* Base thin glow backing to keep the border looking vibrant */}
                    <div className="absolute -inset-[1.5px] rounded-full bg-emerald-400 dark:bg-primary opacity-40 blur-[1px] z-[-1]" />
                  </>
                )}

                {/* Connecting shimmer overlay */}
                {isConnecting && (
                  <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none z-10">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                  </div>
                )}

                <div className="absolute inset-0 rounded-full overflow-hidden flex items-center justify-center pointer-events-none">
                  <AudioVisualizer analyser={aiAnalyserRef.current} isSpeaking={isAISpeaking} color="#ffffff" mode="circular" className="w-[150%] h-[150%] absolute opacity-30" />
                </div>

                {/* Status Badge */}
                <div className={`absolute -bottom-4 right-8 px-4 py-1.5 ${isConnecting ? 'bg-amber-500' : 'bg-primary'} rounded-full flex items-center gap-2 shadow-lg hover:scale-105 transition-all z-20 text-primary-foreground`}>
                  <div className={`w-2 h-2 rounded-full bg-primary-foreground ${isConnecting ? 'animate-pulse' : (isListening && !isMuted ? 'animate-pulse' : '')}`} />
                  <span className="text-[10px] font-bold text-primary-foreground tracking-widest uppercase">{isConnecting ? 'CONNECTING' : agentState}</span>
                </div>
              </div>
            </div>

            {/* Name and Tagline */}
            <div className="text-center z-10 mb-16 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
              {(isConnecting || !interviewerRevealed) ? (
                <>
                  <h2 className="text-2xl font-extrabold text-foreground mb-2 flex items-center justify-center gap-2">
                    Assigning you an Interviewer
                    <span className="inline-flex gap-0.5">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  </h2>
                  <p className="text-sm font-medium text-slate-400">Please wait while we set things up</p>
                </>
              ) : (
                <>
                  <h2 className="text-3xl font-extrabold text-foreground mb-1">{interviewer.name}</h2>
                  <p className="text-sm font-semibold text-slate-400">{interviewer.tagline}</p>
                </>
              )}
            </div>

          </div>
        </>
      ) : (
        /* --- IDE/WORKSPACE VIEW (Image 2) --- */
        <div className="flex-1 flex flex-col h-full bg-card relative">
          <div className="h-10 px-4 flex items-center justify-between w-full flex-shrink-0 z-50 bg-card relative">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="font-bold text-foreground text-lg tracking-tight">prepxai</span>
              </div>
            </div>

            {/* Center Timer */}
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
              <div className="flex items-center gap-1 px-2.5 py-1 border border-border rounded-full text-[11px] font-bold text-foreground bg-card">
                <Icon name="Timer" size={13} className="text-primary" />
                <span className="font-mono">{formatTime(timeElapsed)}</span>
              </div>
              {showAutoEndCountdown && (
                <div className="flex items-center gap-1 px-2.5 py-1 border border-red-500/30 rounded-full text-[10px] font-bold text-red-600 dark:text-red-400 bg-red-500/10" role="status" aria-live="polite">
                  <Icon name="Clock3" size={13} />
                  <span>Auto-ends {formatTime(autoEndRemainingSeconds)}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => {
                    setShowIssueReportModal(true);
                    setShowReportHint(false);
                  }}
                  className="flex items-center justify-center rounded-full border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 px-2.5 py-1.5 text-rose-500 transition-colors shadow-sm active:scale-95"
                  aria-label="Report Issue"
                  title="Report Issue"
                >
                  <Icon name="Flag" size={13} className="text-rose-500" />
                  <span className="ml-1.5 text-[10px] font-semibold tracking-wider uppercase text-rose-500">Report</span>
                </button>
                {showReportHint && (
                  <div className="absolute top-10 right-0 z-50 w-64 bg-slate-900/95 backdrop-blur-md text-white p-3 rounded-2xl shadow-2xl border border-slate-700/80 flex items-start gap-2.5 animate-fade-in-up">
                    <div className="p-1.5 bg-rose-500/20 text-rose-400 rounded-xl shrink-0 mt-0.5">
                      <Icon name="Flag" size={14} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-white text-xs tracking-tight">Facing any issue?</p>
                      <p className="text-[11px] font-normal text-slate-300 mt-0.5 leading-snug">
                        Report voice problems, AI hallucinations, or glitches anytime here.
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowReportHint(false);
                      }}
                      className="text-slate-400 hover:text-white p-0.5 rounded-full hover:bg-slate-800 transition-colors"
                    >
                      <Icon name="X" size={13} />
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={handleToggleTranscript}
                className={`flex items-center justify-center rounded-full border px-2.5 py-1.5 transition-colors ${showTranscript ? 'border-primary/20 bg-primary/10 text-primary' : 'border-border bg-card text-slate-400 hover:text-foreground'}`}
                aria-label="Toggle captions"
                title="Toggle captions"
              >
                <Icon name="Subtitles" size={14} />
                <span className="ml-1.5 text-[10px] font-black tracking-widest uppercase">CC</span>
              </button>
              <ThemeModeToggle />
              <button
                onClick={handleEndInterview}
                className="px-3 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-md font-bold text-[11px] tracking-wider transition-colors border border-red-500/20"
              >
                End Session
              </button>
            </div>
          </div>

          <div className="flex-1 flex overflow-hidden min-h-0 pb-14 relative">
            <UnifiedWorkspace
              problemData={currentQuestion}
              problemStatement={currentQuestion?.description || currentQuestion?.question || currentQuestion?.title || "Waiting for problem statement..."}
              interviewType={interviewType}
              sessionId={sessionId}
              onCodeChange={handleCodeChange}
              onCodeSubmit={handleCodeSubmit}
              onCodeRun={handleCodeRun}
              onWhiteboardUpdate={handleWhiteboardUpdate}
              executionResult={executionResult}
              isExecuting={isExecuting}
              isEnding={isEnding}
              onInternalCopy={(text) => { lastInternalCopyRef.current = text; }}
            />

            <div className="h-20 bg-transparent pointer-events-none" />
          </div>
        </div>
      )}

      {/* Global Dynamic Captions (High Z-Index) */}
      <DynamicCaption text={currentCaption} isVisible={showTranscript} />


      {/* End Interview Modal */}
      <Modal
        isOpen={showEndInterviewModal}
        onClose={() => setShowEndInterviewModal(false)}
        title="End Interview Session"
        titleClassName="text-base font-bold text-slate-900"
        size="xs"
        footer={
          <>
            <Button
              variant="outline"
              size="sm"
              className="hover:bg-slate-100 hover:text-slate-900 border-slate-200 text-slate-700"
              onClick={() => setShowEndInterviewModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={confirmEndInterview}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isEnding}
            >
              <Icon name="PhoneOff" size={14} className="mr-2" />
              {isEnding ? 'Ending...' : 'End Session'}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <Icon name="AlertTriangle" size={20} className="text-red-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-foreground mb-1">
              Are you sure you want to end?
            </h3>
            <p className="text-xs text-slate-500 mb-2">
              Your session will be finalized and a detailed performance report will be generated.
            </p>
          </div>
        </div>
      </Modal>

      {/* Pause Overlay */}
      {isPaused && (
        <div className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-card rounded-xl p-8 flex flex-col items-center max-w-md text-center shadow-2xl border border-border animate-fade-in">
            <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-6">
              <Icon name="Pause" size={32} className="text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-foreground">Interview Paused</h2>
            <p className="text-slate-400 mb-8">
              Your session is currently paused. The timer has stopped and your progress is saved.
              Click below to resume when you're ready.
            </p>
            <Button
              onClick={handleResume}
              variant="default"
              size="lg"
              className="w-full h-12 text-lg"
            >
              <Icon name="Play" size={20} className="mr-2" />
              Resume Interview
            </Button>
          </div>
        </div>
      )}

      {showIssueReportModal && (
        <IssueReportModal
          sessionId={sessionId}
          onClose={() => setShowIssueReportModal(false)}
        />
      )}

      {showFeedbackModal && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={handleFeedbackSkip}
          onSubmit={handleFeedbackSubmit}
          isSubmitting={isSubmittingFeedback}
        />
      )}
    </div>
  );
};

export default LiveInterviewPanel;
